import { Box, Rating, Typography, Button } from "@mui/material";
import { useState } from "react";
import TextField from "components/common/TextField";
import { useTypewriter } from "components/hooks/useTypewriter";
import { CurrentUser } from "protogen/auth_pb";

interface Props {
  onSubmit: (text: string, starCount: number, displayName?: string) => void;
  loading?: boolean;
  onCancel?: () => void;
  showDisplayName?: boolean;
  currentUser?: CurrentUser;
}

export default ({
  onSubmit,
  loading = false,
  onCancel,
  showDisplayName = false,
  currentUser,
}: Props) => {
  const [text, setText] = useState("");
  const [starCount, setStarCount] = useState<number>(5);
  const [displayName, setDisplayName] = useState("");

  const placeholderText = useTypewriter({
    texts: [
      "Share your experience with your advisor...",
      "What did you appreciate most?",
      "How has your advisor helped you?",
      "What makes your advisor stand out?",
    ],
    enabled: text.length === 0,
    typingSpeed: 50,
    erasingSpeed: 25,
    pauseDuration: 3000,
  });

  const handleSubmit = () => {
    if (text.trim() && starCount > 0) {
      if (showDisplayName) {
        onSubmit(text, starCount, displayName);
      } else {
        onSubmit(text, starCount);
      }
    }
  };

  const isValid =
    text.trim().length > 0 &&
    starCount > 0 &&
    (!showDisplayName || displayName.trim().length > 0);

  const getDisplayNamePreview = () => {
    if (!currentUser?.firstName || !currentUser?.lastName) return "";
    return `${currentUser.firstName} ${currentUser.lastName.charAt(0)}`;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        width: "100%",
      }}
    >
      {!showDisplayName && currentUser && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography variant="body">
            <strong>Review by:</strong> {getDisplayNamePreview()}
          </Typography>
          <Typography variant="bodySmall" sx={{ color: "text.secondary" }}>
            This review may be shared publicly.
          </Typography>
        </Box>
      )}

      {showDisplayName && (
        <TextField
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Name to display on the review"
          fullWidth
          required
        />
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Typography variant="body" sx={{ fontWeight: 600 }}>
          Rating
        </Typography>
        <Rating
          value={starCount}
          onChange={(_, newValue) => {
            if (newValue !== null) {
              setStarCount(newValue);
            }
          }}
          size="large"
        />
      </Box>

      <TextField
        label="Your Review"
        multiline
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={
          placeholderText || "Share your experience with your advisor..."
        }
        fullWidth
        required
      />

      <Box sx={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || loading}
        >
          {loading ? "Submitting..." : "Submit Review"}
        </Button>
      </Box>
    </Box>
  );
};
